import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Award, Briefcase, Menu, Building2, CircleHelp, FileBadge, FileSignature, FolderOpen, LayoutDashboard, LogOut, Receipt, Settings, Activity, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { GlobalSearch } from "@/components/search/GlobalSearch";

const ROLE_LABEL = { owner: "Admin / Leadership", admin: "Admin / Leadership", pm: "Project Manager", finance: "Finance", team: "Team Member" };
const HIDDEN = { team: ["/payments", "/settings"], pm: ["/settings"], finance: ["/settings"] };
const NAV = [
  { name: "Dashboard", path: "/dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { name: "Internal Team", path: "/people", icon: Users, testid: "nav-people" },
  { name: "Clients", path: "/clients", icon: Building2, testid: "nav-clients" },
  { name: "Conference Contacts", path: "/conference-contacts", icon: Users, testid: "nav-conference-contacts" },
  { name: "Projects", path: "/projects", icon: Briefcase, testid: "nav-projects" },
  { name: "Payments", path: "/payments", icon: Receipt, testid: "nav-payments" },
  { name: "MarksMan Documents", path: "/marksman-documents", icon: Award, testid: "nav-marksman-documents" },
  { name: "Client Onboarding", path: "/client-onboarding", icon: FileBadge, testid: "nav-client-onboarding" },
  { name: "Master Service Agreements", path: "/master-service-agreements", icon: FileSignature, testid: "nav-msa" },
  { name: "All Documents", path: "/documents", icon: FolderOpen, testid: "nav-documents" },
  { name: "Settings", path: "/settings", icon: Settings, testid: "nav-settings" },
  { name: "How to Use", path: "/help", icon: CircleHelp, testid: "nav-help" },
];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [navOpen, setNavOpen] = useState(false);
  return (
    <div className="min-h-screen flex bg-slate-50">
      {navOpen && <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setNavOpen(false)} />}
      <aside className={`w-60 shrink-0 bg-slate-900 text-slate-100 flex flex-col fixed inset-y-0 left-0 z-50 transition-transform lg:translate-x-0 ${navOpen ? "translate-x-0" : "-translate-x-full"}`} data-testid="sidebar" onClick={() => setNavOpen(false)}>
        <div className="px-5 py-6 border-b border-slate-800">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-md bg-teal-500 flex items-center justify-center">
              <Activity className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="font-heading font-bold text-[15px] leading-tight tracking-tight">MarksMan Healthcare</div>
              <div className="text-[11px] text-slate-400 uppercase tracking-wider">Project Hub</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.filter((n) => !(HIDDEN[user?.role] || []).includes(n.path)).map((n) => (
            <NavLink
              key={n.path}
              to={n.path}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-teal-600/90 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              {n.name}
            </NavLink>
          ))}
        </nav>
        <div className="px-4 py-4 border-t border-slate-800">
          <div className="text-sm font-medium truncate" data-testid="current-user-name">{user?.name}</div>
          <div className="text-xs text-slate-400 truncate">{user?.email}</div>
          <div className="text-[11px] text-teal-400 mt-0.5 uppercase tracking-wider">{ROLE_LABEL[user?.role] || user?.role}</div>
          <button
            data-testid="logout-button"
            onClick={async () => { await logout(); navigate("/login"); }}
            className="mt-3 flex items-center gap-2 text-xs text-slate-400 hover:text-white transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 lg:ml-60 min-w-0">
        <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200 px-4 lg:px-8 py-3 flex items-center gap-3" data-testid="app-header">
          <button type="button" className="lg:hidden h-9 w-9 inline-flex items-center justify-center rounded-md border border-slate-200 text-slate-700" onClick={() => setNavOpen(true)} aria-label="Open menu" data-testid="nav-toggle"><Menu className="h-4 w-4" /></button>
          <GlobalSearch />
        </header>
        <div className="px-4 lg:px-8 py-5 lg:py-8 max-w-[1600px]">{children}</div>
      </main>
    </div>
  );
};
